import React, { useEffect, useMemo, useState } from 'react'
import {
  useForm,
  useFieldArray,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form'
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { dateTimeString } from '../../../../../core/functions/dateTime'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import {
  ProductTicketAreaVM,
  ProductTicketFormValues,
  ProductTicketLotCriterion,
  ProductTicketLotVM,
} from '../../../../../core/types/Tickets'
import ConfirmDialog from '../../../../../components/Dialogs/ConfirmDialog'
import ProductTicketAreaDialog from './ProductTicketAreaDialog'
import ProductTicketLotDialog from './ProductTicketLotDialog'
import { lotCriterionConfig } from './ProductTicketConsts'
import TicketController from '../../../../../core/controllers/TicketController'
import LoadingBackdrop from '../../../../../components/Loading/LoadingBackdrop'
import ErrorSnackbar from '../../../../../components/Snackbar/ErrorSnackbar'
import SuccessSnackbar from '../../../../../components/Snackbar/SuccessSnackbar'
import { IErrorResponse } from '../../../../../core/types/ErrorResponse'
import { IconCopy } from '../../../../../core/constants/IconConsts'
import Hosts, { payUrl } from '../../../../../core/functions/hosts'
import CopiedSnackbar from '../../../../../components/Snackbar/CopiedSnackbar'
import {
  TProduct,
  TProductPrice,
  TProductTypePayment,
} from '../../../../../core/types/Product'
import { MultiSelectedItem } from '../../../../../components/Dialogs/MultiSelectedDialog'

const DialogClient = {
  async query(_name: string, _payload: { productId: string }) {
    return TicketController.getByProduct(_payload)
  },
  async execute(
    _name: string,
    _payload: { productId: string } & ProductTicketFormValues
  ) {
    return TicketController.register(_payload)
  },
}

export default function ProductTicketAreasLots({
  productId,
  initialData,
  offers,
  productOrderBumps,
  onSave,
  watch: prodWatch,
  setProductOrderBumps,
  setValue: setProdValue,
}: {
  productId: string
  initialData?: ProductTicketFormValues
  offers: TProductPrice[] | undefined
  productOrderBumps: MultiSelectedItem[]
  onSave?: (data: ProductTicketFormValues) => void | Promise<void>
  watch: UseFormWatch<TProduct>
  setProductOrderBumps: (items: MultiSelectedItem[]) => void
  setValue: UseFormSetValue<TProduct>
}) {
  const form = useForm<ProductTicketFormValues>({
    defaultValues: initialData || { areas: [] },
    mode: 'onBlur',
  })

  const { control, handleSubmit, getValues, setValue, watch, reset } = form
  const areasFA = useFieldArray({ control, name: 'areas' })

  // ===== Dialog State =====
  const [areaDialog, setAreaDialog] = useState<{
    open: boolean
    index: number | null
  }>()
  const [lotDialog, setLotDialog] = useState<{
    open: boolean
    areaIndex: number | null
    lotIndex: number | null
  }>()
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    areaIndex: number | null
    lotIndex: number | null
  }>({ open: false, areaIndex: null, lotIndex: null })
  const [state, setState] = useState<{
    loading: boolean
    success: boolean
    error: string
  }>({ loading: false, success: false, error: '' })
  const [linkCopied, setLinkCopied] = useState('')

  const actionLinkCopied = (
    <React.Fragment>
      <Button
        color="secondary"
        size="small"
        onClick={() => Hosts.openNewBlank(linkCopied)}
        sx={{ color: 'white' }}
      >
        ABRIR LINK
      </Button>
    </React.Fragment>
  )

  // Open/close helpers
  const openCreateArea = () => setAreaDialog({ open: true, index: null })
  const openEditArea = (index: number) => setAreaDialog({ open: true, index })
  const closeArea = () => setAreaDialog({ open: false, index: null })

  const openCreateLot = (areaIndex: number) =>
    setLotDialog({ open: true, areaIndex, lotIndex: null })
  const openEditLot = (areaIndex: number, lotIndex: number) =>
    setLotDialog({ open: true, areaIndex, lotIndex })
  const closeLot = () =>
    setLotDialog({ open: false, areaIndex: null, lotIndex: null })

  const openDeleteArea = (areaIndex: number) =>
    setDeleteDialog({ open: true, areaIndex, lotIndex: null })
  const openDeleteLot = (areaIndex: number, lotIndex: number) =>
    setDeleteDialog({ open: true, areaIndex, lotIndex })
  const closeDelete = () =>
    setDeleteDialog({ open: false, areaIndex: null, lotIndex: null })

  async function persist(values: ProductTicketFormValues) {
    setState({ loading: true, success: false, error: '' })
    const payload = { productId, areas: values.areas }
    console.log({ payload })
    const response = await DialogClient.execute(
      'UpsertAreasLotsCommand',
      payload
    )
    if (response.error) {
      setState({ loading: false, success: false, error: response.error })
    }
    if (onSave) await onSave(values)
    setState({ loading: false, success: true, error: '' })
  }

  async function search() {
    if (productId === '') return
    setState({ loading: true, success: false, error: '' })
    const response = await DialogClient.query('GetAreasLotsCommand', {
      productId,
    })
    const respError = response as IErrorResponse
    if (respError.error) {
      setState({ loading: false, success: false, error: respError.error })
      return
    }
    const data = response as ProductTicketFormValues
    reset(data)
    setState({ loading: false, success: false, error: '' })
  }

  const deleteArea = (i: number): void => {
    return areasFA.remove(i)
  }

  const deleteLot = (i: number, j: number) => {
    const lots = [...getValues(`areas.${i}.lots`)]
    lots.splice(j, 1)
    setValue(`areas.${i}.lots`, lots, {
      shouldDirty: true,
    })
  }

  const copyURL = async (index: number) => {
    const code = watch(`areas.${index}.codeId`)
    const link = `${payUrl}/t/${code}`
    await Hosts.copyTextToClipboard(link)
    setLinkCopied(link)
  }

  useEffect(() => {
    search()
  }, [productId])

  return (
    <>
      <Box width="100%" sx={{ p: 3 }}>
        <Typography
          color="#343948"
          fontWeight={700}
          fontSize="16px"
          lineHeight="20px"
          gutterBottom
        >
          Áreas & Lotes
        </Typography>

        <Card variant="outlined" sx={{ width: '100%', mb: 2 }}>
          <CardContent sx={{ width: '100%' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1">Configuração</Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={openCreateArea}
                sx={{ textTransform: 'none' }}
              >
                Adicionar Área
              </Button>
            </Stack>

            {areasFA.fields.length === 0 && (
              <Alert severity="info" sx={{ width: '100%' }}>
                Nenhuma área configurada ainda.
              </Alert>
            )}

            {areasFA.fields
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((a, i) => (
                <Box
                  key={a.id}
                  width="100%"
                  sx={{
                    border: '1px solid #eee',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Stack
                    width="100%"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                      {getValues(`areas.${i}.name` as const) ||
                        `Área #${i + 1}`}
                    </Typography>
                    <Tooltip title="Copiar URL">
                      <IconButton onClick={() => copyURL(i)}>
                        <img src={IconCopy} alt="copy" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar área">
                      <IconButton onClick={() => openEditArea(i)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover área">
                      <IconButton
                        color="error"
                        onClick={() => openDeleteArea(i)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Table size="small" sx={{ mt: 1, width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Lote</TableCell>
                        <TableCell>Preço</TableCell>
                        <TableCell>Critério</TableCell>
                        <TableCell>Janela</TableCell>
                        <TableCell>Limite</TableCell>
                        <TableCell>Ordem</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => openCreateLot(i)}
                            sx={{ textTransform: 'none' }}
                          >
                            Adicionar Lote
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {watch(`areas.${i}.lots`)
                        .sort((a, b) => a.order - b.order)
                        .map((l, j) => (
                          <TableRow key={l.id ?? `${i}-${j}`}>
                            <TableCell>{l.name}</TableCell>
                            <TableCell>
                              {NumberFunctions.formatMoney(
                                Number(l.offerValue || 0),
                                true
                              )}
                            </TableCell>
                            <TableCell>
                              {lotCriterionConfig[l.criterion].description}
                            </TableCell>
                            <TableCell>
                              {(l.startDate
                                ? dateTimeString(new Date(l.startDate))
                                : '-') +
                                ' → ' +
                                (l.endDate
                                  ? dateTimeString(new Date(l.endDate))
                                  : '-')}
                            </TableCell>
                            <TableCell>{l.unitLimit ?? 0}</TableCell>
                            <TableCell>{l.order}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => openEditLot(i, j)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteLot(i, j)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="contained"
                onClick={handleSubmit(persist)}
                sx={{ textTransform: 'none' }}
              >
                Salvar
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* ===== Dialog Área ===== */}
        <ProductTicketAreaDialog
          open={!!areaDialog?.open}
          initial={useMemo(() => {
            if (areaDialog?.index == null) {
              return {
                name: '',
                description: '',
                capacity: 0,
                displayOrder:
                  areasFA.fields.reduce(
                    (max, field) => Math.max(max, field.displayOrder),
                    0
                  ) + 1,
              } as ProductTicketAreaVM
            }
            return getValues(
              `areas.${areaDialog.index}`
            ) as ProductTicketAreaVM & {
              lots: ProductTicketLotVM[]
            }
          }, [areaDialog, areasFA.fields, getValues])}
          onClose={closeArea}
          onSubmit={(area) => {
            if (areaDialog?.index == null) {
              areasFA.append({ ...area, lots: [] })
            } else {
              setValue(`areas.${areaDialog.index}.name`, area.name, {
                shouldDirty: true,
              })
              setValue(
                `areas.${areaDialog.index}.description`,
                area.description || '',
                { shouldDirty: true }
              )
              setValue(
                `areas.${areaDialog.index}.capacity`,
                Number(area.capacity || 0),
                { shouldDirty: true }
              )
              setValue(
                `areas.${areaDialog.index}.displayOrder`,
                Number(area.displayOrder || 1),
                { shouldDirty: true }
              )
            }
            closeArea()
          }}
        />

        {/* ===== Dialog Lote ===== */}
        <ProductTicketLotDialog
          open={!!lotDialog?.open}
          offers={offers}
          delivery={prodWatch('delivery')}
          productId={prodWatch('id')}
          typePaymentValue={prodWatch('typePayment')}
          productOrderBumps={productOrderBumps}
          setProductOrderBumps={setProductOrderBumps}
          productMaximumQuantity={prodWatch('maximumQuantity')}
          setProdValue={setProdValue}
          initial={useMemo(() => {
            if (lotDialog?.areaIndex == null) return undefined
            if (lotDialog?.lotIndex == null) {
              const aid = getValues(`areas.${lotDialog.areaIndex}.id`)
              const count = getValues(
                `areas.${lotDialog.areaIndex}.lots`
              ).reduce((max, field) => Math.max(max, field.order), 0)
              return {
                areaId: aid,
                name: `Lote ${count + 1}`,
                criterion: ProductTicketLotCriterion.both,
                unitLimit: 0,
                order: count + 1,
                startAt: new Date(),
                endAt: new Date(),
                offerId: '',
                offerCodeId: '',
                offerName: '',
                offerValue: 0,
              } as ProductTicketLotVM
            }
            return getValues(
              `areas.${lotDialog.areaIndex}.lots.${lotDialog.lotIndex}`
            ) as ProductTicketLotVM
          }, [lotDialog, areasFA.fields.length, getValues])}
          onClose={closeLot}
          onSubmit={(lot) => {
            if (lotDialog?.areaIndex == null) return
            if (lotDialog?.lotIndex == null) {
              const lots = [...getValues(`areas.${lotDialog.areaIndex}.lots`)]
              lots.push(lot)
              setValue(`areas.${lotDialog.areaIndex}.lots`, lots, {
                shouldDirty: true,
              })
            } else {
              setValue(
                `areas.${lotDialog.areaIndex}.lots.${lotDialog.lotIndex}`,
                lot,
                { shouldDirty: true }
              )
            }
            closeLot()
          }}
        />

        <ConfirmDialog
          open={deleteDialog.open}
          onClose={closeDelete}
          message={`Deseja realmente deletar ${
            deleteDialog.lotIndex ? 'esse lote' : 'essa área'
          } ?`}
          onYes={() => {
            if (deleteDialog.areaIndex !== null) {
              if (deleteDialog.lotIndex !== null) {
                deleteLot(deleteDialog.areaIndex, deleteDialog.lotIndex)
                return
              }
              deleteArea(deleteDialog.areaIndex)
            }
          }}
        />
      </Box>

      <LoadingBackdrop open={state.loading} />

      <ErrorSnackbar
        open={!!state.error}
        onClose={() => setState((prev) => ({ ...prev, error: '' }))}
      >
        {state.error}
      </ErrorSnackbar>

      <CopiedSnackbar
        open={!!linkCopied}
        onClose={() => setLinkCopied('')}
        action={actionLinkCopied}
      />

      <SuccessSnackbar
        open={state.success}
        onClose={() => setState((prev) => ({ ...prev, success: false }))}
      >
        Registrado com sucesso!
      </SuccessSnackbar>
    </>
  )
}
